import Events16 from '@carbon/icons-react/es/events/16';
import Save16 from '@carbon/icons-react/es/save/16';
import { Col, Row } from 'antd';
import { isEmpty } from 'lodash';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';
import { User, UserType } from 'api/models';
import { PartnerCompany } from 'api/partner/types';
import { Button } from 'components/atoms/button';
import {
  CustomLayoutDrawer,
  CustomLayoutDrawerContent,
  DefaultCustomLayoutDrawerProps,
} from 'components/atoms/drawer';
import { Form } from 'components/atoms/form';
import { CreatableSelect, Select } from 'components/atoms/select';
import { DescriptiveOption } from 'components/atoms/select/custom-components';
import { InviteUserCreateableLabel } from 'components/atoms/select/invite-user-createable-label';
import { toastService } from 'components/molecules/toast';
import { Member, MembersAPI } from 'data/members/api';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { ALL_MEMBERS_QUERY_KEY, ALL_USERS_QUERY_KEY } from 'queries/members';
import { ALL_PARTNER_COMPANIES_KEY } from 'queries/partner';
import { isValidEmail } from 'utils/is-valid-email';
import { usePermissionOptions } from './permission-options';
import { useRolesOptionsForPartnerMembers } from './role-options';

export const Footer = styled.div`
  display: flex;
  gap: var(--spacing-05);
  justify-content: flex-end;
`;
const ErrorMessage = styled.div`
  color: var(--red-60);
`;

interface Props {
  visible: boolean;
  partner: PartnerCompany;
  member?: Member;
  alreadyExistingUsers: User[];
  onClose: () => void;
}
export const ManageTeamMembersDrawer: FC<Props> = ({
  visible,
  onClose,
  alreadyExistingUsers,
  partner,
  member,
}) => {
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const [form] = Form.useForm();
  const isEditMode = member != null;
  const emailIds = useMemo(
    () =>
      (alreadyExistingUsers ?? []).map(({ emailId }) => emailId.toLowerCase()),
    [alreadyExistingUsers],
  );
  const { roleOptions } = useRolesOptionsForPartnerMembers();
  const { permissionOptions } = usePermissionOptions();

  useEffect(() => {
    if (visible && isEditMode) {
      form.setFieldsValue({
        emails: [
          {
            label: member.user.emailId,
            value: member.user.emailId,
          },
        ],
        role: roleOptions.find((x) => x.value === member.user.role?.roleId),
        permission: permissionOptions.find(
          (x) => x.value === member.permissionSet.permissionSetType,
        ),
      });
    }
  }, [form, isEditMode, member, permissionOptions, roleOptions, visible]);
  const [isActionPending, setActionPendingStatus] = useState(false);
  const closeDrawer = useCallback(() => {
    form.resetFields();
    onClose();
  }, [form, onClose]);
  const onInvite = useCallback(() => {
    form.validateFields().then(({ emails, role, permission }) => {
      const payload = emails.map(({ value }) => ({
        user: {
          emailId: value,
          userType: UserType.Partner,
          company: {
            companyId: partner.companyId,
          },
          role: {
            role: role.value,
          },
        },
        permissionSet: {
          permissionSetType: permission.value,
        },
      }));

      setActionPendingStatus(true);
      MembersAPI.inviteMembers(payload)
        .then(async () => {
          toastService({
            title: translate('success'),
            message: translate('settings.team_members.invite.success', {
              number: emails.length,
            }),
          }).success();
          await queryClient.invalidateQueries(ALL_USERS_QUERY_KEY);
          await queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
          await queryClient.invalidateQueries(ALL_PARTNER_COMPANIES_KEY);
        })
        .catch(async (data) => {
          const failedUsers = data.filter(({ status }) => !status);

          if (isEmpty(failedUsers)) {
            toastService({
              title: translate('failure'),
              message: translate('settings.team_members.invite.error'),
            }).error();
          } else {
            const emailsText = failedUsers
              .slice(0, 2)
              .map(({ user: { emailId } }) => emailId)
              .join(', ');
            const countText =
              failedUsers.length > 2
                ? '+'.concat(String(failedUsers.length - 2))
                : undefined;

            toastService({
              title: translate('settings.team_members.invite.partial.title'),
              message: `${emailsText} ${countText ?? ''}`,
            }).error();

            await queryClient.invalidateQueries(ALL_USERS_QUERY_KEY);
            await queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
          }
        })
        .finally(() => {
          closeDrawer();
          setActionPendingStatus(false);
        });
    });
  }, [form, closeDrawer, partner.companyId, queryClient, translate]);
  const onUpdate = useCallback(() => {
    form.validateFields().then(({ role, permission }) => {
      if (!isEditMode) {
        return;
      }
      const formData = {
        user: {
          userId: member.user.userId,
          role: {
            roleId: role.value,
          },
          defaultCapacity: false,
          capacity: 40,
        },
        permissionSet: {
          permissionSetType: permission.value,
        },
      };

      setActionPendingStatus(true);
      MembersAPI.updateMember(formData)
        .then(async () => {
          toastService({
            title: translate('success'),
            message: translate('settings.team_members.edit.success'),
          }).success();
          await queryClient.invalidateQueries(ALL_USERS_QUERY_KEY);
          await queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
        })
        .catch(() => {
          toastService({
            title: translate('failure'),
            message: translate('settings.team_members.edit.error'),
          }).error();
        })
        .finally(() => {
          closeDrawer();
          setActionPendingStatus(false);
        });
    });
  }, [
    closeDrawer,
    form,
    isEditMode,
    member?.user.userId,
    queryClient,
    translate,
  ]);

  return (
    <CustomLayoutDrawer
      {...DefaultCustomLayoutDrawerProps}
      visible={visible}
      width={680}
      onClose={closeDrawer}
    >
      <CustomLayoutDrawerContent>
        <CustomLayoutDrawerContent.Title>
          <FormattedMessage id="partners.team_members.invite.title" />
        </CustomLayoutDrawerContent.Title>
        <CustomLayoutDrawerContent.Content>
          <Form form={form} layout="vertical">
            <Form.Item
              label={useTranslation('settings.team_members.invite.label.email')}
              name="emails"
              rules={[
                {
                  required: true,
                  message: useTranslation(
                    'settings.team_members.invite.validations.email',
                  ),
                },
              ]}
            >
              <CreatableSelect
                isDisabled={isEditMode}
                autoFocus
                showMenuOnOpen={false}
                isMulti
                type="user"
                isClearable
                options={[]}
                formatCreateLabel={(userInput) => (
                  <InviteUserCreateableLabel value={userInput} />
                )}
                isValidNewOption={(input) =>
                  isValidEmail(input) &&
                  !emailIds.includes(input.trim().toLowerCase())
                }
                noOptionsMessage={({ inputValue }) => {
                  if (emailIds.includes(inputValue.trim().toLowerCase())) {
                    return (
                      <ErrorMessage>
                        <FormattedMessage
                          id="settings.team_members.invite.email.errors.existing_user"
                          values={{ email: inputValue }}
                        />
                      </ErrorMessage>
                    );
                  }

                  return <FormattedMessage id="validations.invite_email" />;
                }}
              />
            </Form.Item>
            <Row>
              <Col span="11">
                <Form.Item
                  label={useTranslation(
                    'settings.team_members.invite.label.role',
                  )}
                  name="role"
                  rules={[
                    {
                      required: true,
                      message: useTranslation(
                        'settings.team_members.invite.validations.role',
                      ),
                    },
                  ]}
                >
                  <Select closeMenuOnSelect options={roleOptions} />
                </Form.Item>
              </Col>
              <Col offset="1" span="12">
                <Form.Item
                  label={
                    <>
                      {useTranslation(
                        'settings.team_members.invite.label.permission',
                      )}
                    </>
                  }
                  name="permission"
                  rules={[
                    {
                      required: true,
                      message: useTranslation(
                        'settings.team_members.invite.validations.permission',
                      ),
                    },
                  ]}
                >
                  <Select
                    options={permissionOptions}
                    components={{ Option: DescriptiveOption }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </CustomLayoutDrawerContent.Content>
        <CustomLayoutDrawerContent.Footer>
          <Footer>
            <Button type="tertiary" onClick={closeDrawer}>
              <FormattedMessage id="cancel" />
            </Button>
            {isEditMode ? (
              <Button
                type="primary"
                icon={<Save16 />}
                iconPlacement="right"
                onClick={onUpdate}
                loading={isActionPending}
              >
                <FormattedMessage id="save" />
              </Button>
            ) : (
              <Button
                type="primary"
                icon={<Events16 />}
                iconPlacement="right"
                onClick={onInvite}
                loading={isActionPending}
              >
                <FormattedMessage id="invite" />
              </Button>
            )}
          </Footer>
        </CustomLayoutDrawerContent.Footer>
      </CustomLayoutDrawerContent>
    </CustomLayoutDrawer>
  );
};
